import { api } from "@service/api";
import { IconType } from "react-icons";

import {
  CustomerNotificationStatusEnum,
  CustomerNotificationStatusTypes,
} from "@hooks/dtos/ICustomerNotificationDTO";

import {
  NotificationSubCategoryGroupKeyEnum,
  NotificationSubCategoryGroupKeyTypes,
  NotificationSubCategoryIds,
} from "@hooks/dtos/INotificationSubCategoryDTO";

import {
  FaDollarSign,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaRegEnvelope,
  FaRegEnvelopeOpen,
  FaRegEye,
  FaRegEyeSlash,
  FaTicketAlt,
  FaUserPlus,
} from "react-icons/fa";
import { FaHandshakeAngle, FaRegTrashCan } from "react-icons/fa6";

export const changeNotificationStatus = async ({
  notificationId,
  status,
}: {
  notificationId: number;
  status: CustomerNotificationStatusTypes;
}) => {
  try {
    const params = `?${new URLSearchParams(
      Object.entries({ notificationId: notificationId.toString(), status }),
    ).toString()}`;
    const result = await api.put(`/notification/status${params}`);
    // console.log({ params, data: result.data });
    return result;
  } catch (err: any) {
    throw new Error(err?.response?.data.message || "Ocorreu um erro!");
  }
};

interface INotificationPropertyDTO {
  bgColor: string;
  textColor: string;
  icon: IconType;
}

export const getNotificationProperty = ({
  subCategoryId,
  groupKey,
}: {
  subCategoryId: number;
  groupKey: NotificationSubCategoryGroupKeyTypes;
}): INotificationPropertyDTO => {
  let bgColor: string = "#6c757d";
  let textColor: string = "#fff";
  let icon: IconType = FaExclamationTriangle;

  switch (groupKey) {
    // --- CAMPAIGN STATUS (Rifa/Campaign/Main) --- //
    case NotificationSubCategoryGroupKeyEnum.CAMPAIGN_STATUS:
      icon = FaTicketAlt;

      // --- Color Config --- //
      switch (subCategoryId) {
        case NotificationSubCategoryIds.CAMPAIGN_STATUS_INACTIVE:
          bgColor = "#dc3545";
          break;

        default:
          bgColor = "#4b4a4a";
          break;
      }
      break;

    // --- CAMPAIGN AFFILIATION --- //
    case NotificationSubCategoryGroupKeyEnum.CAMPAIGN_AFF:
      icon = FaUserPlus;

      // --- Color Config --- //
      switch (subCategoryId) {
        case NotificationSubCategoryIds.CAMPAIGN_AFF_REQUEST:
          bgColor = "#0dcaf0";
          break;

        case NotificationSubCategoryIds.CAMPAIGN_AFF_ACCEPT:
          bgColor = "#28a745";
          break;

        case NotificationSubCategoryIds.CAMPAIGN_AFF_REJECT:
          bgColor = "#ffc107";
          break;

        case NotificationSubCategoryIds.CAMPAIGN_AFF_BLOCK:
          bgColor = "#dc3545";
          break;

        default:
          bgColor = "#4b4a4a";
          break;
      }
      break;

    // --- AFFILIATE PROPOSAL --- //
    case NotificationSubCategoryGroupKeyEnum.AFF_PROPOSAL:
      icon = FaHandshakeAngle;

      // --- Color Config --- //
      switch (subCategoryId) {
        case NotificationSubCategoryIds.AFFILIATE_PROPOSAL_REQUEST:
          bgColor = "#0dcaf0";
          break;

        case NotificationSubCategoryIds.AFFILIATE_PROPOSAL_ACCEPT:
          bgColor = "#28a745";
          break;

        case NotificationSubCategoryIds.AFFILIATE_PROPOSAL_REJECT:
          bgColor = "#ffc107";
          break;

        case NotificationSubCategoryIds.AFFILIATE_PROPOSAL_BLOCK:
          bgColor = "#dc3545";
          break;

        default:
          bgColor = "#4b4a4a";
          break;
      }
      break;

    // --- FINANCIAL --- //
    case NotificationSubCategoryGroupKeyEnum.FINANCIAL:
      icon = FaMoneyBillWave;

      // --- Color Config --- //
      switch (subCategoryId) {
        case NotificationSubCategoryIds.FINANCIAL_WITHDRAW_REQUEST:
          bgColor = "#0dcaf0";
          break;

        case NotificationSubCategoryIds.FINANCIAL_WITHDRAW_APPROVED:
          bgColor = "#28a745";
          break;

        case NotificationSubCategoryIds.FINANCIAL_WITHDRAW_DENIED:
          bgColor = "#dc3545";
          break;

        default:
          bgColor = "#4b4a4a";
          break;
      }
      break;

    case NotificationSubCategoryGroupKeyEnum.COMMISSION:
      icon = FaDollarSign;

      // --- Color Config --- //
      switch (subCategoryId) {
        case NotificationSubCategoryIds.COMMISSION_RECEIVED:
          bgColor = "#28a745";
          break;

        default:
          bgColor = "#4b4a4a";
          break;
      }
      break;

    default:
      bgColor = "#6c757d";
      icon = FaExclamationTriangle;
      break;
  }

  return { bgColor, textColor, icon };
};

interface INotificationReadStatusActionDTO {
  text: string;
  bgColor: string;
  textColor: string;
  icon: IconType;
  newStatus: CustomerNotificationStatusTypes;
}

export const getNotificationReadActionStatus = ({
  status,
}: {
  status: CustomerNotificationStatusTypes;
}): INotificationReadStatusActionDTO | undefined => {
  let text: string = "";
  let bgColor: string = "#6c757d";
  let textColor: string = "#fff";
  let icon: IconType;
  let newStatus: CustomerNotificationStatusTypes;

  switch (status) {
    case CustomerNotificationStatusEnum.FAKE_READ:
      text = "Marcar mensagem como não lida";
      bgColor = "#0d6efd";
      icon = FaRegEnvelope;
      newStatus = CustomerNotificationStatusEnum.UNREAD;
      break;

    case CustomerNotificationStatusEnum.READ:
      text = "Marcar mensagem como não lida";
      bgColor = "#0d6efd";
      icon = FaRegEnvelope;
      newStatus = CustomerNotificationStatusEnum.UNREAD;
      break;

    case CustomerNotificationStatusEnum.UNREAD:
      text = "Marcar mensagem como lida";
      bgColor = "#0d6efd";
      icon = FaRegEnvelopeOpen;
      newStatus = CustomerNotificationStatusEnum.READ;
      break;

    default:
      return;
  }
  return { text, bgColor, textColor, icon, newStatus };
};

interface INotificationStatusPropertyDTO {
  text: string;
  description: string;
  bgColor: string;
  textColor: string;
  icon: IconType;
}

export const getNotificationStatusProperty = ({
  status,
}: {
  status: CustomerNotificationStatusTypes;
}): INotificationStatusPropertyDTO => {
  let text: string = "";
  let description: string = "";
  let bgColor: string = "#6c757d";
  let textColor: string = "#fff";
  let icon: IconType;
  switch (status) {
    case CustomerNotificationStatusEnum.FAKE_READ:
    case CustomerNotificationStatusEnum.READ:
      text = "Lida";
      description = "Mensagem lida";
      bgColor = "#28a745";
      textColor = "text-white";
      icon = FaRegEnvelope;
      break;

    case CustomerNotificationStatusEnum.UNREAD:
      text = "Não lida";
      description = "Mensagem não lida";
      textColor = "text-white";
      bgColor = "#ffc107";
      icon = FaRegEnvelopeOpen;
      break;

    case CustomerNotificationStatusEnum.HIDDEN:
      text = "Ocultada";
      description = "Mensagem ocultada";
      textColor = "text-white";
      icon = FaRegEyeSlash;
      break;

    case CustomerNotificationStatusEnum.EXCLUDED:
      text = "Excluída";
      description = "Mensagem excluída";
      textColor = "text-white";
      bgColor = "#dc3545";
      icon = FaRegTrashCan;
      break;

    default:
      text = "Error";
      textColor = "text-white";
      icon = FaExclamationTriangle;
      break;
  }
  return { text, description, bgColor, textColor, icon };
};

interface INotificationHideStatusActionDTO {
  text: string;
  bgColor: string;
  textColor: string;
  icon: IconType;
  newStatus: CustomerNotificationStatusTypes;
}

export const getNotificationHideActionStatus = ({
  status,
}: {
  status: CustomerNotificationStatusTypes;
}): INotificationHideStatusActionDTO => {
  let text: string = "";
  let bgColor: string = "#6c757d";
  let textColor: string = "#fff";
  let icon: IconType;
  let newStatus: CustomerNotificationStatusTypes;

  switch (status) {
    default:
    case CustomerNotificationStatusEnum.READ:
    case CustomerNotificationStatusEnum.UNREAD:
    case CustomerNotificationStatusEnum.FAKE_READ:
      text = "Ocultar esta mensagem";
      icon = FaRegEyeSlash;
      newStatus = CustomerNotificationStatusEnum.HIDDEN;
      break;

    case CustomerNotificationStatusEnum.HIDDEN:
      text = "Desocultar esta mensagem";
      icon = FaRegEye;
      newStatus = CustomerNotificationStatusEnum.FAKE_READ;
      break;
  }
  return { text, bgColor, textColor, icon, newStatus };
};
